import './rater.schedule-rating.scss';

import { TableCell, TableCellProps, TableRow } from '@mui/material';
import { Fragment, ReactNode } from 'react';
import { NoteTargetName, NoteTargetType } from '@converge/graphql';
import { NumberField, RaterNotesButton, RaterNotesRow } from '../common';
import { useFormContext } from 'react-hook-form';
import { hasErrors } from './rater.enterprise-fusion-score';
import { getParsedValue } from '../coverage-rating';
import {
  SR_MAX_ITEM_PERCENTAGE,
  SR_MIN_ITEM_PERCENTAGE,
} from '../data/rater.constants';

interface ITableCellProps extends TableCellProps {
  children?: ReactNode;
}

interface IRaterScheduleRatingDetailsProps {
  uuid: string;
  hasChanged?: boolean;
  percentage?: number | null;
  onChange: (uuid: string, percentage: number) => void;
  minPercentage?: number;
  maxPercentage?: number;
  name?: string | null;
  upsertQuoteIsLoading?: boolean;
  isEditable?: boolean;
  isRequestingRatingUpdate: boolean;
  quoteUuid: string;
}

export const RaterScheduleRatingDetails = ({
  uuid,
  hasChanged,
  percentage,
  onChange,
  minPercentage = SR_MIN_ITEM_PERCENTAGE,
  maxPercentage = SR_MAX_ITEM_PERCENTAGE,
  name,
  upsertQuoteIsLoading,
  isEditable,
  isRequestingRatingUpdate,
  quoteUuid,
}: IRaterScheduleRatingDetailsProps) => {
  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const hasInvalidSelectedPercentage = hasErrors(`${uuid}`, errors);

  const minPercentageDecimal = (minPercentage ?? SR_MIN_ITEM_PERCENTAGE) / 100;
  const maxPercentageDecimal = (maxPercentage ?? SR_MAX_ITEM_PERCENTAGE) / 100;

  const setScheduleRatingError = (name: string) => {
    setError(`${name}`, {
      type: `${name}`,
      message: `Schedule Rating must be a number between ${
        minPercentage ?? SR_MIN_ITEM_PERCENTAGE
      }% and ${maxPercentage ?? SR_MAX_ITEM_PERCENTAGE}%`,
    });
  };

  const handleUpdateSelectedPercentage = (
    value: number | null,
    name: string
  ) => {
    const parsedValue = getParsedValue(value);

    if (parsedValue === percentage) {
      clearErrors(`${name}`);
      return;
    }

    if (parsedValue !== null && parsedValue !== undefined) {
      // Clamp to bounds so state always holds a valid value; show error if input was out of range.
      const clampedValue = Math.min(
        Math.max(parsedValue, minPercentageDecimal),
        maxPercentageDecimal
      );
      if (clampedValue !== parsedValue) {
        setScheduleRatingError(name);
      } else {
        clearErrors(`${name}`);
      }
      onChange(name, clampedValue);
    } else {
      onChange(name, 0);
      clearErrors(`${name}`);
    }
  };

  return (
    <Fragment key={uuid}>
      <TableRow
        sx={{
          backgroundColor: hasChanged ? 'rgb(253, 245, 231, 0.75)' : undefined,
        }}
      >
        <RaterScheduleRatingTableBodyCell width={'35%'}>
          {name}
        </RaterScheduleRatingTableBodyCell>
        <RaterScheduleRatingTableBodyCell width={'15%'}>
          {minPercentage}%
        </RaterScheduleRatingTableBodyCell>
        <RaterScheduleRatingTableBodyCell width={'15%'}>
          {maxPercentage}%
        </RaterScheduleRatingTableBodyCell>
        <RaterScheduleRatingTableBodyCell width={'15%'}>
          <NumberField
            isDisabled={
              upsertQuoteIsLoading || !isEditable || isRequestingRatingUpdate
            }
            isPercentage
            name={uuid}
            value={percentage ?? null}
            onBlur={(value) => handleUpdateSelectedPercentage(value, uuid)}
            error={hasInvalidSelectedPercentage}
          />
        </RaterScheduleRatingTableBodyCell>
        <RaterScheduleRatingTableBodyCell>
          <div className="notes-wrapper">
            <RaterNotesButton
              uuid={quoteUuid ?? ''}
              targetName={NoteTargetName.HasScheduleRating}
              targetType={NoteTargetType.Relationship}
            />
          </div>
        </RaterScheduleRatingTableBodyCell>
      </TableRow>
      <RaterNotesRow
        colSpan={5}
        targetName={NoteTargetName.HasScheduleRating}
        targetType={NoteTargetType.Relationship}
        uuid={quoteUuid ?? ''}
      />
    </Fragment>
  );
};

const RaterScheduleRatingTableBodyCell = ({
  children,
  ...otherProps
}: ITableCellProps) => {
  return (
    <TableCell className="schedule-rating-cell" {...otherProps}>
      {children}
    </TableCell>
  );
};
